import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Clock, Edit, Users, MapPin, BriefcaseMedical, Save, X, Monitor, Image as ImageIcon } from 'lucide-react';
import { MAP_OPTIONS, ITEM_OPTIONS } from '../../data/studioPresets';
import { PageHeader, Button, BackToListLink } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useAuth } from '../../contexts/AuthContext';
import { Scenario } from '../../types/admin';
import { useScenarioDetail, scenarioKeys } from '@/hooks/useScenario';
import {
	getScenarioBasicInfo,
	updateScenarioBasicInfo,
	getDeviceTypeList,
	findScenarioCategoryIdFromMyScenarioList,
} from '@/services/scenarioService';
import {
	buildUpdateScenarioBasicInfoPayload,
	tryExtractScenarioCategoryIdFromBasicInfo,
	convertScenarioBasicInfoToScenario,
	pickHandoverTextForLocale,
} from '@/utils/scenarioUtils';
import { useApiLanguage } from '@/hooks/useApiLanguage';

interface ScenarioDetailProps {
    scenarioId?: string;
    hideBreadcrumbs?: boolean;
    /** true면 시나리오 정보 편집 UI 비활성 (마스터·오픈클래스 세션 등) */
    disableScenarioEdit?: boolean;
}

export default function ScenarioDetail({ scenarioId, hideBreadcrumbs = false, disableScenarioEdit = false }: ScenarioDetailProps): React.ReactElement {
    const { t, i18n } = useTranslation('scenario');
    const apiLang = useApiLanguage();
    const { id: paramId } = useParams<{ id: string }>();
    const id = scenarioId || paramId;

    const location = useLocation();
    const { hasRole } = useAuth();
    const queryClient = useQueryClient();

    // State for edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [scenarioData, setScenarioData] = useState<Scenario | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [thumbLoadFailed, setThumbLoadFailed] = useState(false);

    // 🌐 Real API 조회 (상세 조회는 바로 사용 가능)
    const scenarioIdNum = useMemo(() => {
        if (!id) return 0;
        const num = parseInt(id, 10);
        return isNaN(num) ? 0 : num;
    }, [id]);

    const {
        data: realApiData,
        isLoading: realLoading,
        error: realError,
    } = useScenarioDetail(scenarioIdNum, {
        enabled: scenarioIdNum > 0,
    });


    useEffect(() => {
        setThumbLoadFailed(false);
    }, [id, realApiData]);

    // Real API 데이터 처리 — API 데이터만 사용
    useEffect(() => {
        if (realApiData) {
            const info = realApiData.data;
            const converted = convertScenarioBasicInfoToScenario(info, {
                scenarioId: scenarioIdNum,
                locale: i18n.language,
            });

            // handoverNote 파싱
            const handoverBody: string = pickHandoverTextForLocale(info.handoverNote, i18n.language);

            // API 필드를 string으로 직접 사용
            const roleStr = typeof info.role === 'string' ? info.role.trim() : '';
            const equipStr = typeof info.equipmentItem === 'string' ? info.equipmentItem.trim() : '';
            const deviceModelStr = typeof info.deviceModel === 'string' ? info.deviceModel.trim() : '';

            const merged: Scenario = {
                ...converted,
                handover: handoverBody,
                role: roleStr,
                equipmentItem: equipStr,
                deviceModel: deviceModelStr,
                organization: converted.organization,
                authors: converted.authors,
            };
            setScenarioData(merged);
        }
    }, [realApiData, id, scenarioIdNum, i18n.language]);


    const isAdmin = location.pathname.startsWith('/admin');
    const isMaster = location.pathname.startsWith('/master');
    const isManagementView = isAdmin || isMaster;
    const basePath = isMaster ? '/master' : '/admin';

    const displayData = scenarioData;

    if (realLoading) {
        return (
            <div className="p-8">
                <p className="text-gray-600">{t('detail.loading')}</p>
            </div>
        );
    }
    if (realError) {
        return (
            <div className="p-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">
                        {t('detail.errorPrefix')} {realError instanceof Error ? realError.message : t('detail.errorUnknown')}
                    </p>
                    <p className="text-sm text-red-600 mt-2">
                        {t('detail.errorRefresh')}
                    </p>
                </div>
            </div>
        );
    }
    if (!displayData) {
        return (
            <div className="p-8">
                <p className="text-gray-600">{t('detail.notFound')}</p>
                {scenarioIdNum > 0 && <p className="text-sm text-gray-500 mt-2">ID: {id}</p>}
            </div>
        );
    }

    // Check edit permission
    // Check edit permission - Relaxed for testing
    // const isAuthor = user?.name === displayData.ContributedBy;
    const canEdit = !disableScenarioEdit && (hasRole('admin') || hasRole('master'));

    const handleSave = async () => {
        if (scenarioIdNum <= 0) {
            setSaveError('유효하지 않은 시나리오 ID입니다.');
            return;
        }
        setSaveError(null);
        setIsSaving(true);
        try {
            const info = await getScenarioBasicInfo(scenarioIdNum, apiLang);
            const objectiveTitles =
                displayData.keyTasks?.length
                    ? displayData.keyTasks
                    : (displayData.learningObjectives ?? []);

            const infoAny = info as unknown as Record<string, unknown>;
            let modeValuePatch: string | undefined;
            if (typeof infoAny.modeValue === 'string' && (infoAny.modeValue as string).trim()) {
                modeValuePatch = (infoAny.modeValue as string).trim();
            }

            const fromInfoCat = tryExtractScenarioCategoryIdFromBasicInfo(info);
            let resolvedCategoryId =
                fromInfoCat != null && fromInfoCat > 0 ? fromInfoCat : undefined;

            if (resolvedCategoryId == null) {
                try {
                    const id = await findScenarioCategoryIdFromMyScenarioList(scenarioIdNum);
                    if (id != null) resolvedCategoryId = id;
                } catch {
                    /* 목록 조회 실패 */
                }
            }

            const overrides = {
                handoverNote: displayData.handover ?? '',
                objectiveTitles,
            };
            const patchFallbacks = {
                scenarioCategoryId: resolvedCategoryId,
                modeValue: modeValuePatch,
            };

            let payload = buildUpdateScenarioBasicInfoPayload(info, overrides, patchFallbacks, apiLang);
            if (!payload.deviceTypeIdList || payload.deviceTypeIdList.length === 0) {
                try {
                    const devRes = await getDeviceTypeList();
                    const list =
                        (devRes as { deviceTypeList?: { deviceTypeId?: number; id?: number }[] })
                            ?.deviceTypeList ??
                        (
                            devRes as {
                                data?: { deviceTypeList?: { deviceTypeId?: number; id?: number }[] };
                            }
                        )?.data?.deviceTypeList ??
                        [];
                    const ids = list
                        .map((d) => {
                            const id = d.deviceTypeId ?? d.id;
                            return id != null && Number.isFinite(Number(id))
                                ? Math.trunc(Number(id))
                                : null;
                        })
                        .filter((id): id is number => id !== null);
                    if (ids.length > 0) {
                        payload = buildUpdateScenarioBasicInfoPayload(info, overrides, {
                            ...patchFallbacks,
                            deviceTypeIdList: ids,
                        }, apiLang);
                    }
                } catch {
                    /* 장치 목록 실패 시 그대로 PATCH */
                }
            }

            await updateScenarioBasicInfo(scenarioIdNum, payload);
            await queryClient.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioIdNum) });
            await queryClient.refetchQueries({ queryKey: scenarioKeys.detail(scenarioIdNum) });
            await queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
            setIsEditing(false);
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = async () => {
        setIsEditing(false);
        setSaveError(null);
        if (scenarioIdNum > 0) {
            await queryClient.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioIdNum) });
            await queryClient.refetchQueries({ queryKey: scenarioKeys.detail(scenarioIdNum) });
        }
    };

    const breadcrumbs = isManagementView
        ? [
            { label: t('detail.managementBreadcrumb'), to: `${basePath}/scenarios` },
            { label: displayData.title, active: true }
        ]
        : [
            { label: 'Class', to: ROUTES.COMMON.CLASS_LIST },
            { label: displayData.title, active: true }
        ];

    const mapInfo = displayData.mapId
        ? MAP_OPTIONS.find(opt => opt.id === displayData.mapId)
        : MAP_OPTIONS.find(opt => opt.name === displayData.map);
    const displayMapName = mapInfo ? mapInfo.name : displayData.map ?? '';

    const pageBreadcrumbs = breadcrumbs.map(({ label, to }) => ({ label, link: to }));
    const backToListTo = isManagementView ? `${basePath}/scenarios` : ROUTES.COMMON.SCENARIOS;

    return (
        <div className="max-w-[1460px] mx-auto px-4 py-8 space-y-6 pb-20">
            {!hideBreadcrumbs && (
                <>
                    <BackToListLink to={backToListTo} className="mb-4">
                      {t('detail.backToList')}
                    </BackToListLink>
                    <PageHeader
                    title={displayData.title}
                        breadcrumbs={pageBreadcrumbs}
                    />
                </>
            )}



            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
                {/* 왼쪽: 메인 콘텐츠 */}
                <div className="space-y-6 min-w-0">
                    {/* 미디어 영역 - 항상 표시 */}
                    <div className="w-full aspect-video rounded-lg overflow-hidden bg-blue-50 border border-blue-100 flex items-center justify-center">
                        {displayData.thumbnail && !thumbLoadFailed ? (
                            <img
                                src={displayData.thumbnail}
                                alt={displayData.title}
                                className="w-full h-full object-cover"
                                onError={() => setThumbLoadFailed(true)}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center text-gray-400">
                                <ImageIcon size={48} className="mb-2" />
                                <span className="text-sm">{t('detail.noImage')}</span>
                            </div>
                        )}
                    </div>

                    {/* 학습 목표 */}
                    <section>
                        <h2 className="text-lg font-bold text-gray-900 mb-3">{t('detail.learningObjectives')}</h2>
                        <p className="text-gray-700 text-base leading-relaxed">
                            {(displayData.learningObjectives ?? []).length > 0
                                ? displayData.learningObjectives!.join(', ')
                                : t('detail.noLearningObjectives')}
                        </p>
                    </section>

                    {/* 인계 정보 */}
                    <section>
                        <h2 className="text-lg font-bold text-gray-900 mb-3">{t('detail.handover')}</h2>
                        {isEditing ? (
                            <textarea
                                className="w-full h-32 p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={displayData.handover}
                                onChange={(e) => setScenarioData({ ...displayData, handover: e.target.value })}
                            />
                        ) : (
                            <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
                                {displayData.handover || t('detail.noHandover')}
                            </p>
                        )}
                    </section>

                </div>

                {/* 오른쪽: 사이드바 */}
                <aside className="lg:sticky lg:top-4 h-fit space-y-3">
                    <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                        <div className="flex items-start gap-3">
                            <Clock size={18} className="text-gray-500 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t('detail.estimatedTime')}</p>
                                <p className="text-sm font-medium text-gray-900">{displayData.duration}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Monitor size={18} className="text-gray-500 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500 mb-1">{t('detail.supportedDevices')}</p>
                                <p className="text-sm font-medium text-gray-900">{displayData.deviceModel || '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Users size={18} className="text-gray-500 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500 mb-1">{t('detail.participatingRoles')}</p>
                                <p className="text-sm font-medium text-gray-900">{displayData.role || '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin size={18} className="text-gray-500 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t('detail.background')}</p>
                                <p className="text-sm font-medium text-gray-900">{displayMapName || '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <BriefcaseMedical size={18} className="text-gray-500 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500 mb-1">{t('detail.equipment')}</p>
                                <p className="text-sm font-medium text-gray-900">{displayData.equipmentItem || '-'}</p>
                            </div>
                        </div>
                    </div>

                    {canEdit && (
                        <div className="flex flex-col items-end gap-2">
                            {saveError && (
                                <p className="text-sm text-red-600 text-right max-w-full" role="alert">
                                    {saveError}
                                </p>
                            )}
                            {isEditing ? (
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        onClick={() => void handleSave()}
                                        size="sm"
                                        disabled={isSaving}
                                        className="flex items-center gap-2"
                                    >
                                        <Save size={14} />
                                        {isSaving ? '저장 중…' : t('detail.save')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => void handleCancel()}
                                        size="sm"
                                        disabled={isSaving}
                                        className="flex items-center gap-2"
                                    >
                                        <X size={14} />
                                        {t('detail.cancel')}
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setSaveError(null);
                                        setIsEditing(true);
                                    }}
                                    className="flex items-center gap-2"
                                >
                                    <Edit size={14} />
                                    {t('detail.editInfo')}
                                </Button>
                            )}
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}
